'use client';
// Shell GED — ConnectionBar minimale (design : tout masqué sauf Paramétrage RQ,
// profil et « Retour au site ») + sous-header module + sidebar sticky 222px.
import React, { useEffect, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { createPortal } from 'react-dom';
import { pastilleDe } from '@/lib/couleurs-personne';
import { DeconnexionAuto } from '@/shared/DeconnexionAuto';
import { useGed, reposerScrolls } from '@/lib/store';
import { cheminDe } from '@/lib/adresse';
import { useAdresse } from '@/lib/useAdresse';
import { AgrandirColonne } from '@/components/AgrandirColonne';
import { GED_ROLES, jourIso, isoJour, aujourdhuiIso } from '@/lib/meta';
import { I, CountPill, COUCHES, useZonesAuto, useInfobulle, Modale } from '@/components/ui';
import { PiedActions } from '@/shared/PiedActions';
import { ModuleSwitcher, moduleAccessFor } from '@/shared/ModuleSwitcher';
import { CentreTaches } from '@/shared/CentreTaches';
import { GommetteNC, type ContexteNC } from '@/shared/GommetteNC';
import { SettingsModal } from '@/components/screens/Settings';
import { ncOuverte, DeclareForm } from '@/components/screens/NC';
import { RecherchePalette } from '@/components/RecherchePalette';
import { TravailEnCours } from '@/components/TravailEnCours';
import { AnnonceDuJour } from '@/components/AnnonceDuJour';
import { PanneauFiche } from '@/components/PanneauFiche';
import { usePageLarge } from '@/shared/Tableau';
import { DestockageFenetre } from '@/components/DestockageFenetre';
import { LoginView } from '@/components/LoginView';
import { ACCUEIL, FAMILLES_NAV, famillesBarre, familleDe, vueDeNavigation, libelleVue, droitsNav, type DroitsNav, type EntreeNav } from '@/lib/navigation';
import type { GedView } from '@/lib/types';
import { useUrlsModules } from '@/shared/urlsModulesClient';
import { PanneauProfil } from '@/shared/PanneauProfil';

/** Ce que la personne EST, pas ce que le moteur lui prête : ses rôles portés
 *  (sans « lecteur », qui n'ouvre rien), précédés de « Super admin » pour le
 *  profil technique qui a accès à tout (Ines, 16/10/2026). */
function libelleRolesSession(session: { profile: string; roles: string[]; rolesPortes?: string[] }): string {
  const portes = (session.rolesPortes ?? session.roles).filter((r) => r !== 'lecteur').map((r) => GED_ROLES[r]?.label || r);
  if (session.profile === 'superadmin') portes.unshift('Super admin — accès à tout');
  return portes.join(' · ') || 'Aucun rôle';
}

/**
 * Source de NC déduite de l'écran d'où l'on déclare.
 *
 * Ce n'est pas un raccourci de saisie : c'est la seule information de
 * classement que l'application peut donner SANS se tromper. Faire deviner le
 * processus au déclarant produirait des NC mal classées — invisibles dans la
 * cartographie — là où « à qualifier » les rend visibles et traitables.
 */
function contexteNC(view: GedView): ContexteNC {
  const parEcran: Partial<Record<GedView, ContexteNC>> = {
    equip: { source: 'equipement', libelle: 'Équipements / métrologie' },
    fourn: { source: 'reactif', libelle: 'Fournisseurs & réactifs' },
    // Une NC déclarée depuis ce registre porte sur une PRESTATION (compte rendu
    // incomplet, délai de rendu dépassé), pas sur un réactif : la ranger en
    // source « réactif » l'aurait mise avec les ruptures de stock, là où
    // personne ne la cherchera en revue du §6.7. `NC_SOURCES` n'a pas de source
    // « prestation sous-traitée » et en inventer une ici ne suffirait pas — le
    // vocabulaire est aussi contrôlé côté moteur. On retient donc « à
    // qualifier », qui est fait pour ça : le déclarant décrit, le RQ classe, et
    // la NC reste visible au lieu d'être mal rangée. Le libellé, lui, dit d'où
    // elle vient.
    soustraitants: { source: 'aqualifier', libelle: 'Laboratoires sous-traitants' },
    /* Déclarer depuis le carnet : le secteur PRÉ-ANALYTIQUE, parce que c'est
       ce qu'on y signale — une prescription sans échantillon, une identité
       discordante, un échantillon remis hors horaires. Le correspondant, lui,
       se choisit dans le formulaire. */
    correspondants: { source: 'preanalytique', libelle: 'Annuaire' },
    annuaire: { source: 'preanalytique', libelle: 'Annuaire' },
    conventions: { source: 'preanalytique', libelle: 'Conventions' },
    cqtdb: { source: 'cqi', libelle: 'Qualité analytique' },
    cqi: { source: 'cqi', libelle: 'Contrôle interne (CQI)' },
    cqisaisie: { source: 'cqi', libelle: 'Contrôle interne (CQI)' },
    eeq: { source: 'eeq', libelle: 'Évaluation externe (EEQ)' },
    cqeenvois: { source: 'eeq', libelle: 'Évaluation externe (EEQ)' },
    cal: { source: 'equipement', libelle: 'Calibrations' },
    mat: { source: 'reactif', libelle: 'Réactifs de contrôle et de calibration' },
    analytes: { source: 'cqi', libelle: 'Analytes & valeurs usuelles' },
    metro: { source: 'equipement', libelle: 'Équipements / métrologie' },
    maintenances: { source: 'equipement', libelle: 'Maintenance préventive' },
    relevetemp: { source: 'equipement', libelle: 'Relevés de température' },
    list: { source: 'documentaire', libelle: 'Documents' },
    doc: { source: 'documentaire', libelle: 'Documents' },
    recl: { source: 'reclamation', libelle: 'Réclamations' },
    hab: { source: 'rh', libelle: 'Habilitations' },
    // Depuis l'écran des critères d'acceptation, une NC déclarée à la gommette
    // est pré-analytique à coup sûr — c'est le seul écran du module où la
    // source se déduit sans risque de se tromper.
    criteres: { source: 'preanalytique', libelle: 'Réception des échantillons' },
  };
  return parEcran[view] || {};
}


/**
 * Menu du profil — le rond aux initiales, redevenu un BOUTON (12/08/2026).
 *
 * Il n'ouvrait rien : c'était un `<span>` avec une infobulle, alors que le même
 * rond est cliquable dans le module Manuel. Il porte désormais « mon espace » —
 * ce qui ne concerne QUE la personne connectée : ses congés, son planning, son
 * dossier RH, et la demande de congé. Ces trois écrans ont quitté la barre
 * latérale : ils y voisinaient avec le planning de tout le laboratoire et la
 * paie, alors qu'ils ne parlent que de soi.
 *
 * Les conditions de visibilité sont celles de la barre, à l'identique
 * (`droitsNav`) : déplacer une entrée ne lui donne aucun droit nouveau.
 */
function MenuProfil({ initials, titreProfil }: { initials: string; titreProfil: string }) {
  const { session, go, rhAcces } = useGed();
  const pastilleMoi = pastilleDe(session.key, session.couleur);
  const [ouvert, setOuvert] = useState(false);
  /* Le PANNEAU de profil — le même que dans le catalogue et sur le site
     (07/09/2026) : mot de passe, appareils de confiance, et les entrées de ce
     menu reprises sous « Mon espace ». Le menu reste pour l'accès en un clic. */
  const [profilOuvert, setProfilOuvert] = useState(false);
  const boite = React.useRef<HTMLDivElement | null>(null);
  const d = droitsNav(session, !!rhAcces?.acces);

  useEffect(() => {
    if (!ouvert) return undefined;
    const dehors = (e: MouseEvent) => {
      if (boite.current && !boite.current.contains(e.target as Node)) setOuvert(false);
    };
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', dehors);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', dehors); document.removeEventListener('keydown', echap); };
  }, [ouvert]);

  /* MES DEMANDES D'ÉCHANGE EN ATTENTE (RQ, 04/10/2026) : la même pastille que
     l'onglet Échanges et « Mon planning », sur l'entrée du menu — et un point sur
     le rond, pour qu'on sache qu'il y a quelque chose à voir sans ouvrir le menu.
     Relu à chaque ouverture du menu. Seulement celles où je suis demandeur ou
     remplaçant : le serveur rend tout au rôle rh. */
  const [mesEchanges, setMesEchanges] = useState(0);
  useEffect(() => {
    if (!session.key) return undefined;
    let vivant = true;
    void fetch('/api/ged/ged/rh/echanges', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { echanges?: { statut: string; demandeur: string; remplacant: string }[] } | null) => {
        if (!vivant || !d) return;
        setMesEchanges((d.echanges || []).filter((e) => e.statut === 'demandee' && (e.demandeur === session.key || e.remplacant === session.key)).length);
      })
      .catch(() => { /* pastille indisponible : le menu reste sans */ });
    return () => { vivant = false; };
  }, [session.key, ouvert]);

  const entrees: { icon: string; label: string; aide?: string; faire: () => void; pastille?: number }[] = [];
  entrees.push({ icon: 'lock', label: 'Mon profil & sécurité', aide: 'Mot de passe, appareils de confiance, sessions', faire: () => setProfilOuvert(true) });
  if (d.section('mesconges')) {
    /* « Demander un congé » RETIRÉ du menu le 14/08/2026 (décision de la RQ) :
       le formulaire vit déjà dans « Mes congés », et le doublon coûtait plus
       qu'il ne servait — deux chemins pour le même geste, dont l'un ouvrait un
       formulaire par-dessus l'écran qu'on venait de quitter. */
    entrees.push({ icon: 'history', label: 'Mes congés', aide: 'Mes demandes, leur suite, et le formulaire de demande', faire: () => go('mesconges') });
  }
  entrees.push({ icon: 'history', label: 'Mon planning', aide: mesEchanges ? `Mes affectations — ${mesEchanges} échange(s) en attente du RH` : 'Mes affectations sur quatre semaines', faire: () => go('monplanning'), pastille: mesEchanges });
  entrees.push({ icon: 'clock', label: 'Mes présences', aide: 'Ce que j’ai badgé et ce qui a été validé', faire: () => go('mespresences') });
  entrees.push({ icon: 'clipboardCheck', label: 'Mon parcours', aide: 'De l’accueil au maintien : où j’en suis, et qui doit agir', faire: () => go('monparcours') });
  if (d.section('dossierRh')) {
    entrees.push({ icon: 'req', label: 'Mon dossier RH', aide: 'Contrat, matricule, solde de congés', faire: () => go('mondossier') });
  }

  return (
    <div ref={boite} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu" aria-expanded={ouvert} title={`${titreProfil} — mon espace`}
        aria-label={`Mon espace — ${session.name}${mesEchanges ? ` — ${mesEchanges} échange(s) en attente` : ''}`}
        className="cb-btn"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
          /* Le ROND COLORÉ de la personne (RQ, 04/10/2026) — sa couleur du
             planning et du calendrier, pour qu'on se reconnaisse d'un écran à
             l'autre. Le liseré blanc marque l'ouverture du menu. */
          borderRadius: '50%', background: pastilleMoi.background,
          color: pastilleMoi.color, fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)',
          border: 'none', boxShadow: ouvert ? '0 0 0 2px #fff' : 'none', cursor: 'pointer', padding: 0, position: 'relative',
        }}>
        {initials}
        {mesEchanges > 0 && (
          <span aria-hidden style={{ position: 'absolute', top: -2, insetInlineEnd: -2, width: 10, height: 10, borderRadius: '50%', background: 'var(--amber-500)', boxShadow: '0 0 0 2px var(--navy-900, #0B1426)' }} />
        )}
      </button>
      {ouvert && (
        <div role="menu" style={{
          position: 'absolute', top: 38, insetInlineEnd: 0, zIndex: 500, minWidth: 262,
          background: 'var(--surface-raised, #fff)', color: 'var(--text-body)',
          border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
          boxShadow: '0 12px 32px rgba(11,20,38,0.18)', overflow: 'hidden',
        }}>
          <div style={{ padding: '11px 14px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-sunken)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{session.name}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>
              {libelleRolesSession(session)}
            </div>
          </div>
          {entrees.map((e) => (
            <button key={e.label} role="menuitem"
              onClick={() => { setOuvert(false); e.faire(); }}
              className="ged-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'start',
                padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, color: 'var(--text-body)',
              }}>
              <I n={e.icon} s={15} sw={1.9} />
              <span style={{ flex: 1 }}>
                {e.label}
                {e.aide && <span style={{ display: 'block', fontSize: 12.5, fontWeight: 400, color: 'var(--text-muted)' }}>{e.aide}</span>}
              </span>
              {!!e.pastille && (
                <span aria-label={`${e.pastille} en attente`}
                  style={{ minWidth: 18, padding: '0 6px', borderRadius: 'var(--radius-pill)', background: 'var(--amber-500)', color: '#fff', fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center' }}>
                  {e.pastille}
                </span>
              )}
            </button>
          ))}
          {/* La déconnexion ferme la session : elle est SÉPARÉE des entrées de
              consultation par un trait, et posée en dernier. Elle traînait
              jusqu'ici en clair dans la barre, à un clic de distance des écrans
              — un geste qu'on ne veut pas déclencher en visant autre chose. */}
          <button role="menuitem"
            onClick={async () => {
              setOuvert(false);
              await fetchEcriture('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
              // Retour à la connexion de l'application Qualité, pas au site
              // public (RQ, 04/10/2026) : sans session, la racine affiche
              // LoginView — on se reconnecte là où l'on travaillait.
              window.location.href = '/';
            }}
            className="ged-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'start',
              padding: '9px 14px', background: 'none', border: 'none', borderTop: '1px solid var(--border-default)',
              cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
              color: 'var(--red-600, #C0362C)',
            }}>
            <I n="logout" s={15} sw={1.9} />
            <span style={{ flex: 1 }}>Déconnexion</span>
          </button>
        </div>
      )}
      {profilOuvert && createPortal(
        <PanneauProfil
          onFermer={() => setProfilOuvert(false)}
          sousTitre={titreProfil}
          /* L'onglet « Mon espace » du panneau est SUPPRIMÉ (RQ, 04/10/2026) : il
             recopiait les entrées du menu (congés, planning, présences, dossier RH),
             qu'on vient de quitter pour ouvrir ce panneau. Sans entrées, le panneau
             ne montre que l'identité et la Sécurité. */
          entrees={[]}
          notifier={(m) => useGed.getState().showToast(m)}
          apresDeconnexionTotale={() => { window.location.href = '/'; }}
        />,
        document.body,
      )}
    </div>
  );
}

/**
 * La flèche « revenir » (12/08/2026) — l'équivalent du bouton « Précédent » que
 * le navigateur ne peut pas rendre ici, faute d'adresse par écran.
 *
 * Elle ne se confond pas avec le fil d'Ariane (« Revenir à BIO24-NC-2026-036 »,
 * sous le titre) : celui-là dit d'où l'on TIENT la fiche ouverte, et se vide
 * quand on change de sujet. Celle-ci remonte simplement les écrans traversés,
 * sans rien juger — y compris ceux atteints par la barre ou la recherche.
 *
 * Absente tant qu'il n'y a rien derrière : une flèche grisée en permanence
 * n'apprend rien et occupe le seul endroit où l'œil cherche une action.
 */
function FlecheRetour() {
  const historique = useGed((s) => s.historique);
  const avenir = useGed((s) => s.avenir);
  const reculer = useGed((s) => s.reculer);
  const avancer = useGed((s) => s.avancer);
  // Pas de retour anticipé : au tout premier écran, les deux flèches sont là,
  // grisées. Les faire apparaître en cours de route aurait reproduit, en plus
  // discret, le défaut qu'on corrige — une fonction qu'on ne trouve pas parce
  // qu'elle n'était pas là quand on l'a cherchée.
  const rond = (actif: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30,
    border: '1px solid rgba(255,255,255,0.18)', background: 'transparent', color: '#fff',
    borderRadius: '50%', cursor: actif ? 'pointer' : 'default', flex: 'none',
    opacity: actif ? 1 : 0.35,
  });
  const precedent = historique[historique.length - 1];
  const suivant = avenir[avenir.length - 1];
  /*
   * LES DEUX FLÈCHES SONT TOUJOURS LÀ (corrigé le 14/08/2026).
   *
   * La flèche « suivant » n'apparaissait qu'après un retour — au motif qu'un
   * bouton sans destination est un bouton mort. C'était raisonner en
   * développeur : la RQ l'a CHERCHÉE et ne l'a pas trouvée, parce que tout
   * navigateur montre les deux flèches côte à côte, celle de droite grisée.
   * Une place vide n'apprend rien non plus, et fait douter de l'existence de
   * la fonction. On garde donc la paire, et l'infobulle dit pourquoi la
   * seconde est éteinte.
   */
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button onClick={precedent ? reculer : undefined} disabled={!precedent} className="cb-btn"
        title={precedent ? `Revenir à ${libelleVue(precedent.view)}` : 'Aucune page précédente'}
        aria-label={precedent ? `Revenir à ${libelleVue(precedent.view)}` : 'Aucune page précédente'}
        style={rond(!!precedent)}>
        <I n="chevron-left" s={16} />
      </button>
      <button onClick={suivant ? avancer : undefined} disabled={!suivant} className="cb-btn"
        title={suivant
          ? `Revenir à ${libelleVue(suivant.view)} (annuler le retour)`
          : 'Aucune page suivante — cette flèche s’active après un retour en arrière'}
        aria-label={suivant ? `Avancer vers ${libelleVue(suivant.view)}` : 'Aucune page suivante'}
        style={rond(!!suivant)}>
        <I n="chevron-right" s={16} />
      </button>
    </span>
  );
}

function ConnectionBar() {
  // Adresses des autres modules résolues à l'exécution (posées par le layout).
  const { site: SITE_URL, manuel: MANUEL_URL } = useUrlsModules();
  const { session, openSettings } = useGed();
  const isRQ = session.roles.includes('rq');
  // Les initiales de la fiche du personnel d'abord (celles du planning), sinon celles du nom.
  const initials = session.initiales || (session.name || '?').replace(/^Dr\s+/i, '').split(/\s+/).map((p) => p[0] || '').slice(0, 2).join('').toUpperCase();
  // Les rôles ont quitté le sous-en-tête (supprimé) pour cette infobulle : ils
  // se consultent, ils ne se surveillent pas — ils ne changent qu'au
  // paramétrage. Rendus en toutes lettres, et non en sigles : « RED » n'aide
  // personne qui ne connaît pas déjà la nomenclature.
  const titreProfil = [session.name, libelleRolesSession(session)].filter(Boolean).join(' — ');
  /* LA BARRE PREND LA COULEUR DE LA PERSONNE CONNECTÉE (RQ, 04/10/2026) : la
     teinte foncée de son rond (celle des initiales) — on voit d'un coup d'œil
     si la session ouverte sur un poste partagé est la sienne. Sans session, la
     barre reste navy. Clarté 26 % : le texte blanc garde son contraste. */
  const fondBarre = session.authenticated ? pastilleDe(session.key, session.couleur).color : 'var(--navy-900)';
  return (
    <div style={{ background: fondBarre, color: '#fff', transition: 'background .2s' }}>
      {/* Mêmes gabarits que le site et le catalogue (retour d'Inès, 17/08) :
          conteneur, gouttière et hauteur identiques — la barre navy doit être
          LA même d'un module à l'autre. */}
      <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '8px var(--gutter)', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', minHeight: 44 }}>
        <ModuleSwitcher current="ged" urls={{ site: SITE_URL, manuel: MANUEL_URL, ged: 'current' }} access={moduleAccessFor(session.profile)} gedUrl="current" />
        <FlecheRetour />
        {/* Recherche transversale : à gauche du profil, avant les actions —
            c'est un point de départ, pas une commande. */}
        <span style={{ marginInlineStart: 'auto' }}><RecherchePalette /></span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
          <MenuProfil initials={initials} titreProfil={titreProfil} />
          {/* Entre le profil et les paramètres, au même endroit dans les trois
              modules : ce que l'application attend de moi. */}
          <CentreTaches urls={{ site: SITE_URL, manuel: MANUEL_URL, ged: '' }} moduleCourant="ged" />
          {(isRQ || session.roles.includes('rh')) && (
            <button onClick={() => openSettings()} title="Paramétrage" className="cb-btn" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: '1px solid rgba(255,255,255,0.18)', background: 'transparent', color: '#fff', borderRadius: '50%', cursor: 'pointer' }}>
              <I n="gear" s={16} />
            </button>
          )}
          {/* La déconnexion a rejoint le menu du profil (12/08/2026) : elle
              ferme la session, elle n'a rien à faire à découvert dans la barre. */}
        </span>
      </div>
    </div>
  );
}

// Le module s'appelle « Qualité » à l'écran (le sous-en-tête qui le disait a
// été supprimé — il coûtait une bande pleine largeur pour une information
// fixe). Les identifiants TECHNIQUES restent `ged` (routes /api/ged, useGed,
// session.apps.ged, GED_ROLES) : les renommer serait un risque de régression
// pour un gain nul — l'écart nom affiché / nom technique est assumé, et
// documenté ici pour le prochain lecteur.

function NavItem({ view, icon, label, badge, badgeColor, activeView, onGo, replie, groupee }: {
  view: GedView; icon: string; label: string; badge?: number; badgeColor?: string;
  activeView: GedView; onGo: (v: GedView) => void; replie?: boolean;
  /** Rendue à l'intérieur d'une famille : légèrement décalée sous son titre. */
  groupee?: boolean;
}) {
  // la fiche document / éditeur / revue restent rattachés à « Documents »
  const active = vueDeNavigation(activeView) === view;
  /* L'étiquette du rail replié — 48 icônes sans un mot, dont plusieurs se
     ressemblent. Rendue au portail : la colonne défile, une bulle dessinée
     dedans serait rognée. Voir `useInfobulle`. */
  const infobulle = useInfobulle(label, !!replie);
  /* ── Un vrai LIEN, et pas un bouton (14/08/2026) ────────────────────────
     Les 51 entrées de cette barre étaient des `<button>`. Conséquence : ni
     Ctrl+clic, ni clic-milieu, ni « ouvrir dans un nouvel onglet » du menu
     contextuel, ni survol montrant la destination — quatre gestes que tout
     le monde connaît et qui ne faisaient rien ici.

     Le `href` est réel et fonctionne SANS JavaScript (la route attrape-tout
     sert l'application à n'importe quelle adresse). Le `onClick` intercepte
     le clic ORDINAIRE pour naviguer sans rechargement ; il laisse
     délibérément passer le clic modifié (Ctrl, ⌘, Maj) et le clic-milieu,
     que le navigateur doit traiter LUI-MÊME. Intercepter tout, c'est
     reproduire le bouton avec l'apparence d'un lien — pire que le bouton,
     puisqu'on promet un comportement qu'on ne tient pas. */
  return (
    <a
      href={cheminDe(view)}
      onClick={(e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onGo(view);
      }}
      className="ged-nav" data-active={active ? '1' : '0'}
      {...(replie ? infobulle.liens : {})}
      /* `aria-label` porte le nom pour le lecteur d'écran même repliée. Le
         `title` natif, lui, DISPARAÎT quand le rail est replié : c'est
         l'infobulle du socle qui prend le relais (~150 ms, positionnée,
         contrastée), et laisser les deux ferait apparaître deux étiquettes
         l'une sur l'autre. Dépliée, le libellé est écrit : ni l'un ni l'autre. */
      aria-label={label}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        justifyContent: replie ? 'center' : 'flex-start', textAlign: 'start',
        fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        border: 'none', borderRadius: 'var(--radius-md)', textDecoration: 'none',
        /* ── L'icône ne se déplace pas quand la barre s'ouvre ────────────────
           Repliée, la barre fait 44 px et l'icône était CENTRÉE (abscisse 22).
           Dépliée, elle passait à `padding-left: 11` — et à 20 pour une entrée
           groupée. L'icône sautait donc de plusieurs pixels, et de deux valeurs
           différentes selon la ligne : à l'ouverture, toute la colonne semblait
           se réorganiser.

           Le retrait de 9 px sur les entrées groupées est assumé : leur
           appartenance est déjà portée par le titre de famille au-dessus, et
           elle ne valait pas une colonne d'icônes en escalier. */
        padding: replie ? '9px' : groupee ? `7px 11px 7px ${ICONE_GAUCHE}px` : `9px 11px 9px ${ICONE_GAUCHE}px`,
        background: active ? 'var(--cyan-50)' : 'transparent',
        color: active ? 'var(--color-primary-active)' : 'var(--text-body)',
        position: 'relative',
      }}>
      <I n={icon} s={15} sw={1.9} />
      {!replie && <span style={{ flex: 1 }}>{label}</span>}
      {/* ⚠️ LE NOMBRE, MÊME REPLIÉ (relevé du 27/08/2026, P4a).
          Le rail posait une pastille MUETTE de 7 px : on voyait qu'il y avait
          « quelque chose » sans savoir combien — donc sans pouvoir décider si
          ça valait le détour. Or c'est précisément le compteur qui fait ouvrir
          l'écran : trois demandes en attente ne se traitent pas comme trente.
          La pastille chiffrée tient dans 44 px ; elle déborde volontairement du
          coin de l'icône, qui n'a pas de texte à recouvrir. */}
      {infobulle.bulle}
      {badge !== undefined && (replie
        ? (badge > 0 && (
          <span style={{
            position: 'absolute', top: 1, insetInlineEnd: 1,
            minWidth: 15, height: 15, padding: '0 3px',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 'var(--radius-pill)', background: badgeColor || 'var(--red-500)',
            color: '#fff', fontSize: 9.5, fontWeight: 700, fontFamily: 'var(--font-mono)',
            // Un liseré de la couleur du fond détache la pastille de l'icône,
            // qu'elle chevauche — sans lui les deux se confondent en une tache.
            boxShadow: '0 0 0 1.5px var(--surface-card, #fff)',
          }}>{badge > 99 ? '99+' : badge}</span>
        ))
        : <CountPill n={badge} color={badgeColor} />)}
    </a>
  );
}

/** Vrai dans un champ de saisie — le raccourci `[` ne doit pas s'y déclencher. */
function dansUnChamp(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

const CLE_REPLI = 'bio24-ged-sidebar-repliee';

/**
 * Largeur du contenu, CONSTANTE quel que soit l'état de la barre latérale.
 *
 * Largeur intérieure du conteneur − 222 (barre dépliée) − 24 (gouttière) :
 * c'est la valeur du cas le plus étroit, et la retenir dans les deux cas est
 * ce qui empêche la page de se redessiner quand on replie. Elle est MESURÉE
 * (`useLargeurContenu`) et non plus posée : jusqu'au 21/10/2026, le nombre
 * écrit (986, calculé pour 1280) bridait aussi les écrans élargis à 1760 —
 * le planning et les présences n'avaient jamais eu leur largeur. 986 reste la
 * valeur d'avant la première mesure.
 */
const LARGEUR_BARRE = 222;
const GOUTTIERE = 24;
const LARGEUR_CONTENU = 986;

function useLargeurContenu(cadre: React.RefObject<HTMLDivElement | null>) {
  const [largeur, setLargeur] = useState<number | null>(null);
  useEffect(() => {
    const el = cadre.current;
    if (!el) return;
    const mesurer = () => {
      /* En bande (téléphone), une seule colonne : rien à retrancher ; le repli
         de 986 px, plus large que l'écran, ne bride rien. */
      if (window.matchMedia(REQUETE_BANDE).matches) { setLargeur(null); return; }
      const o = getComputedStyle(el);
      setLargeur(el.clientWidth - parseFloat(o.paddingLeft) - parseFloat(o.paddingRight) - LARGEUR_BARRE - GOUTTIERE);
    };
    mesurer();
    const ro = new ResizeObserver(mesurer);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cadre]);
  return largeur;
}

/* ── ACCORDÉON STRICT : UNE SEULE FAMILLE OUVERTE (06/10/2026) ───────────────
 *
 * Le retour de la RQ : « je me perds dans la barre tellement il y a de lignes ».
 * La coupe du 12/09 avait ramené chaque famille sous dix entrées, mais elle
 * n'avait pas touché au mécanisme : REPLIER NE CONTRAIGNAIT RIEN. On dépliait
 * trois familles pour comparer, elles le restaient — et le repli mémorisé
 * reconduisait cette dérive d'un jour sur l'autre. La barre revenait donc à
 * quarante lignes à défiler, soit l'état d'avant la coupe.
 *
 * Désormais ouvrir une famille referme les autres : la barre ne dépasse jamais
 * ~19 lignes (8 titres + 10 entrées au plus + l'accueil épinglé).
 *
 * ⚠️ NOUVELLE CLÉ, et l'ancienne (`…-familles-repliees`) est délibérément
 * ABANDONNÉE sans migration : elle portait la liste des familles FERMÉES, celle
 * -ci porte l'unique famille OUVERTE. Relire l'ancienne sous la nouvelle
 * sémantique ouvrirait, pour tous ceux qui avaient déjà un repli écrit, la
 * première famille de leur liste de fermées. Repartir de « tout fermé » est la
 * valeur par défaut d'origine (relevé SYS-15 du 26/08/2026), et la famille de
 * l'écran courant s'ouvre de toute façon à l'arrivée.
 */
const CLE_FAMILLE_OUVERTE = 'bio24-ged-nav-famille-ouverte';

/**
 * La famille ouverte au montage — `null` = toutes repliées.
 *
 * ⚠️ AU PREMIER USAGE, TOUT EST REPLIÉ (relevé SYS-15 du 26/08/2026 : « barre
 * de ~40 entrées, 6 familles dépliées, le premier écran se coupe au milieu d'un
 * titre de famille »). Ce n'est pas un repli du contenu : la famille de l'écran
 * courant s'ouvre toute seule à l'arrivée (l'ajustement plus bas), donc on voit
 * toujours où l'on est.
 *
 * Un identifiant inconnu (famille supprimée ou renommée depuis) est traité comme
 * `null` : sans ce filtre, la barre s'ouvrirait sur rien et le chevron du titre
 * concerné paraîtrait cassé.
 */
function lireFamilleOuverte(): string | null {
  if (typeof window === 'undefined') return null;
  const brut = localStorage.getItem(CLE_FAMILLE_OUVERTE);
  return brut && FAMILLES_NAV.some((f) => f.id === brut) ? brut : null;
}

/** En-tête d'une famille : bouton de repli, titre, et le total des alertes qu'elle contient. */
/**
 * Abscisse du bord gauche de l'icône, barre DÉPLIÉE.
 *
 * Repliée, la barre fait 44 px et l'icône (15 px) y est centrée : son bord
 * gauche tombe donc à (44 − 15) / 2 ≈ 14,5. En reprenant la même valeur une
 * fois dépliée, l'icône occupe exactement la même colonne dans les deux états
 * — c'est ce qui fait qu'à l'ouverture, rien ne bouge horizontalement.
 *
 * ⚠️ Elle est liée à la largeur du rail (44 px, `Shell`) et à la taille des
 * glyphes (15 px). Changer l'une sans l'autre remet le sautillement.
 */
const ICONE_GAUCHE = 14;


/** Vrai quand la barre latérale est passée en BANDE HORIZONTALE.
 *
 *  Le seuil (980 px) est celui de `globals.css` : deux valeurs qui dériveraient
 *  donneraient une barre en ligne rendue avec la logique de la colonne, ou
 *  l'inverse — un état que personne ne saurait reproduire. */
const REQUETE_BANDE = '(max-width: 980px)';

function useBandeHorizontale() {
  /* `useSyncExternalStore` et non `useState` + effet : lire la media-query dans
     un effet impose un `setState` synchrone au montage, donc un second rendu en
     cascade à chaque ouverture d'écran — et le lint le refuse, à raison. Ici la
     valeur est LUE à la demande, l'abonnement ne sert qu'à re-rendre.
     L'instantané serveur vaut `false` : le rendu serveur ne connaît aucune
     largeur, et partir de la colonne évite un écart d'hydratation visible. */
  return React.useSyncExternalStore(
    (reagir) => {
      const mq = window.matchMedia(REQUETE_BANDE);
      mq.addEventListener('change', reagir);
      return () => mq.removeEventListener('change', reagir);
    },
    () => window.matchMedia(REQUETE_BANDE).matches,
    () => false,
  );
}

/** Une famille de la barre HORIZONTALE : un bouton, et son menu qui s'ouvre
 *  en longueur — comme un menu « Fichier » ou « Édition ».
 *
 *  ── Pourquoi ce composant, et pas seulement du CSS ──────────────────────────
 *
 *  En bande, les 17 entrées se suivaient à plat et les séparateurs de famille
 *  étaient masqués : il fallait faire défiler la bande sur trois écrans pour
 *  atteindre la dernière, sans jamais savoir de quelle famille on approchait.
 *  Grouper est une décision de RENDU, pas de mise en forme — d'où le composant.
 *
 *  ⚠️ Le menu est en `position: fixed`, JAMAIS en absolu dans la bande : la
 *  bande porte `overflow-x: auto`, qui ROGNE tout enfant qui en dépasse. Un
 *  menu absolu s'ouvrirait donc à l'intérieur d'une barre de 48 px de haut,
 *  c'est-à-dire nulle part. Il est positionné à partir du rectangle du bouton,
 *  et repositionné à chaque ouverture.
 */
function MenuFamille({ titre, icon, entrees, courante, alertes, rendreEntree }: {
  titre: string; icon: string; entrees: readonly EntreeNav[]; courante: boolean; alertes: number;
  rendreEntree: (e: EntreeNav, groupee: boolean) => React.ReactNode;
}) {
  const [ouvert, setOuvert] = React.useState(false);
  const [pos, setPos] = React.useState<{ x: number; y: number } | null>(null);
  const btn = React.useRef<HTMLButtonElement | null>(null);

  const basculer = () => {
    const r = btn.current?.getBoundingClientRect();
    if (r) {
      // Bridé à la largeur de l'écran : ouvert près du bord droit, un menu de
      // 240 px sortirait de la page et deviendrait inatteignable au doigt.
      setPos({ x: Math.min(r.left, window.innerWidth - 252), y: r.bottom + 4 });
    }
    setOuvert((o) => !o);
  };

  React.useEffect(() => {
    if (!ouvert) return;
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false); };
    // Un clic AILLEURS ferme — y compris sur une entrée du menu, qui navigue :
    // sans cela le menu resterait ouvert par-dessus l'écran qu'on vient
    // d'appeler, et il faudrait un second geste pour voir ce qu'on a demandé.
    const surClic = () => setOuvert(false);
    document.addEventListener('keydown', surEchap);
    // `capture` : la navigation se joue au clic de l'entrée, on ferme après.
    document.addEventListener('click', surClic);
    window.addEventListener('resize', surClic);
    return () => {
      document.removeEventListener('keydown', surEchap);
      document.removeEventListener('click', surClic);
      window.removeEventListener('resize', surClic);
    };
  }, [ouvert]);

  return (
    <>
      <button
        ref={btn} type="button" onClick={(e) => { e.stopPropagation(); basculer(); }}
        aria-expanded={ouvert} aria-haspopup="menu" title={titre}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, flex: '0 0 auto',
          fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: courante ? 700 : 600,
          color: courante ? 'var(--color-primary)' : 'var(--text-body)',
          background: ouvert ? 'var(--cyan-50)' : 'transparent',
          border: 'none', borderRadius: 'var(--radius-sm)', padding: '7px 10px',
          cursor: 'pointer', whiteSpace: 'nowrap',
        }}>
        <I n={icon} s={14} sw={2} />{titre}
        {alertes > 0 && (
          <span style={{ minWidth: 16, height: 16, padding: '0 4px', borderRadius: 'var(--radius-pill)', background: 'var(--red-500, #DC2626)', color: '#fff', fontSize: 10.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{alertes}</span>
        )}
        {/* `chevron-bas` retourné, et non `chevron-up` : ce nom n'existe pas
            dans le jeu d'icônes, et `Icon` rend alors du VIDE sans rien dire —
            le chevron aurait simplement disparu. */}
        <I n="chevron-bas" s={12} style={{ transform: ouvert ? 'rotate(180deg)' : undefined, transition: 'transform var(--dur-fast) var(--ease-out)' }} />
      </button>
      {/* ⚠️ PORTÉ SUR `document.body`, et c'est la seule façon que ça marche.
          Le menu est en `position: fixed` — donc, croit-on, positionné par
          rapport à la fenêtre. Il ne l'est pas : la bande horizontale vit dans
          un conteneur `position: sticky` QUI PORTE UN `z-index`, et cela suffit
          à créer un CONTEXTE D'EMPILEMENT. Le menu y est enfermé : son
          `z-index: 120` ne vaut plus que dans ce contexte-là, dont la valeur
          propre est 3 — il passait donc derrière le contenu de la page et
          derrière la barre de connexion (80).
          Symptôme signalé le 18/08 : « en mobile je ne peux pas dérouler les
          onglets, comme s'il se cachait derrière la fenêtre principale ». Le
          menu s'ouvrait bel et bien ; il était dessous.
          Le portail le sort de l'arbre : plus aucun ancêtre ne peut le brider,
          ni par empilement ni par `overflow`. */}
      {ouvert && pos && typeof document !== 'undefined' && createPortal((
        <div role="menu" aria-label={titre} onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed', left: pos.x, top: pos.y, zIndex: 120,
            minWidth: 240, maxWidth: 300, maxHeight: '70vh', overflowY: 'auto',
            display: 'flex', flexDirection: 'column', gap: 1,
            background: 'var(--surface-card, #fff)', border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', padding: 6,
          }}>
          {entrees.map((e) => rendreEntree(e, false))}
        </div>
      ), document.body)}
    </>
  );
}

/* ── TITRE DE FAMILLE : UN PALIER, PAS UNE LIGNE DE PLUS (06/10/2026) ────────
 *
 * Retour de la RQ : « je voudrais voir la différence entre le titre et les
 * rubriques, actuellement toutes les couleurs sont identiques ». Le constat
 * était juste, et mesurable : le titre était en `--text-muted` (`--gray-600`,
 * #546879) et les entrées en `--text-body` (`--gray-700`, #3E4F5E) — deux gris
 * VOISINS de la même échelle. À 10,5 px capitales contre 13 px, l'écart de
 * taille portait seul la hiérarchie, et il ne suffisait pas : la colonne se
 * lisait comme une suite de lignes égales.
 *
 * ⚠️ LE TITRE EST DÉSORMAIS PLUS SOMBRE QUE SES ENTRÉES, et c'est volontaire —
 * c'est le repère qu'on balaie des yeux pour trouver un bloc ; les entrées sont
 * le détail qu'on lit ensuite. Capitales de 10,5 px en `--text-strong` : à la
 * moitié de la taille d'une entrée, l'encre plus dense ne peut pas se lire
 * comme un écran sélectionné (celui-là porte un FOND `--cyan-50`).
 *
 * ⚠️ CE N'EST PAS UN CODE COULEUR PAR FAMILLE — la piste écartée avec la RQ le
 * 12/09. Les huit titres portent le même traitement ; ce qui est distingué,
 * c'est le NIVEAU (titre / entrée), pas la famille.
 *
 * Le filet de 1 px au-dessus ferme le bloc précédent. Il est omis sur la
 * première famille : juste sous le tableau de bord épinglé, il aurait séparé
 * l'accueil de la barre plutôt que deux familles entre elles.
 */
function TitreFamille({ titre, icon, ouverte, courante, alertes, premiere, onToggle, onSurvol, onQuitte }: {
  titre: string; icon: string; ouverte: boolean; courante: boolean; alertes: number;
  /** Première famille de la barre : pas de filet au-dessus. */
  premiere?: boolean;
  onToggle: () => void;
  onSurvol: (r: DOMRect) => void;
  onQuitte: () => void;
}) {
  return (
    <button
      type="button" onClick={onToggle} aria-expanded={ouverte}
      onMouseEnter={(e) => onSurvol(e.currentTarget.getBoundingClientRect())} onMouseLeave={onQuitte}
      title={ouverte ? `Replier ${titre}` : `Déplier ${titre}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'start',
        /* Le titre de famille porte une icône de 13 px : pour que SON centre
           tombe sur la même colonne que celles des entrées (13 px au lieu de
           15 → un pixel de plus à gauche), son retrait vaut ICONE_GAUCHE + 1. */
        marginTop: premiere ? 10 : 12,
        padding: `${premiere ? 5 : 10}px 8px 5px ${ICONE_GAUCHE + 1}px`,
        /* La famille OUVERTE porte un bandeau creux : son titre coiffe alors
           visiblement ses entrées, qui n'ont pas de fond. Avec l'accordéon
           strict il y en a au plus un dans toute la barre — il répond donc
           aussi à « où suis-je ? ». Pas de confusion possible avec l'écran
           sélectionné, qui est en `--cyan-50` et en minuscules de 13 px. */
        background: ouverte ? 'var(--surface-sunken)' : 'transparent',
        cursor: 'pointer', fontFamily: 'var(--font-sans)',
        // `border: 'none'` d'abord, le filet ensuite : l'ordre des clés décide.
        border: 'none',
        /* Filet TOUJOURS de 1 px, transparent quand il ne doit pas se voir :
           l'alterner avec `none` décalerait la barre d'un pixel à chaque repli.
           Il ferme le bloc précédent — inutile sous le bandeau d'une famille
           ouverte, qui sépare déjà, et sous l'accueil épinglé (première). */
        borderTop: `1px solid ${premiere || ouverte ? 'transparent' : 'var(--border-subtle)'}`,
        borderRadius: 'var(--radius-sm)',
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
        /* Repliée alors qu'elle contient l'écran courant : le titre prend la
           couleur d'accent — la barre continue de répondre à « où suis-je ? »
           sans forcer la famille à rester ouverte (retour d'Inès du 17/08 :
           « Personnel & plannings » paraissait impossible à replier). */
        color: courante && !ouverte ? 'var(--color-primary)' : 'var(--text-strong)',
      }}>
      <I n={icon} s={13} sw={2} />
      <span style={{ flex: 1, minWidth: 0 }}>{titre}</span>
      {/* Repliée, une famille ne doit pas cacher une alerte : le total remonte
          sur son titre. Ouverte, il ferait doublon avec les pastilles. */}
      {!ouverte && alertes > 0 && (
        <span aria-label={`${alertes} à traiter`} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--red-500)' }} />
      )}
      <I n={ouverte ? 'chevron-bas' : 'chevron-right'} s={13} sw={2} style={{ opacity: 0.7 }} />
    </button>
  );
}

export function Sidebar({ replie, repliee, onToggle, onFocusDeplie }: {
  replie: boolean; repliee: boolean; onToggle: () => void; onFocusDeplie: (v: boolean) => void;
}) {
  const { view, go, docs, demandes, session, ncs, actions, recls, lots, cqis, cartesCqi, recapCqi, eeqs, rhAcces, rhAffectations, rhAbsences, mesAbsences, rhJournees, rhPaie } = useGed();
  const isRQ = session.roles.includes('rq');
  // Repli des familles : lu au montage, réécrit à chaque changement. Aucun
  // aller-retour serveur — c'est une préférence d'affichage, pas un réglage.
  const [familleOuverte, setFamilleOuverte] = useState<string | null>(lireFamilleOuverte);
  // Survol d'une famille repliée : ses rubriques s'affichent à côté de la barre,
  // sans rien décaler. Le départ de la souris attend, le temps de rejoindre le panneau.
  const [survol, setSurvol] = useState<{ id: string; haut?: number; bas?: number; hauteur: number; gauche: number } | null>(null);
  const fermeSurvol = useRef<number | undefined>(undefined);
  const ouvreSurvol = (id: string, r: DOMRect) => {
    window.clearTimeout(fermeSurvol.current);
    const gauche = r.right + 8;
    // Famille basse : le panneau monte, il ne défile pas.
    if (r.top > window.innerHeight / 2) setSurvol({ id, bas: window.innerHeight - r.bottom, hauteur: r.bottom - 12, gauche });
    else setSurvol({ id, haut: r.top, hauteur: window.innerHeight - r.top - 12, gauche });
  };
  const fermeSurvolDiffere = () => {
    fermeSurvol.current = window.setTimeout(() => setSurvol(null), 150);
  };
  const gardeSurvol = () => window.clearTimeout(fermeSurvol.current);
  useEffect(() => {
    // `removeItem` et non la chaîne « null » : rien d'écrit se relit comme
    // « tout replié », qui EST la valeur par défaut — une chaîne à analyser de
    // plus serait une occasion de divergence pour un gain nul.
    if (familleOuverte) localStorage.setItem(CLE_FAMILLE_OUVERTE, familleOuverte);
    else localStorage.removeItem(CLE_FAMILLE_OUVERTE);
  }, [familleOuverte]);
  // Naviguer vers un écran DÉPLIE sa famille : on ne doit jamais arriver sur un
  // écran invisible dans sa propre barre. C'est ce qui remplace l'ancienne
  // ouverture forcée permanente — l'ouverture est un événement (l'arrivée),
  // plus un état : la famille reste ensuite repliable à la main.
  /* ⚠️ AJUSTÉ PENDANT LE RENDU, PLUS DANS UN EFFET (02/09/2026).
   *
   * C'était un `useEffect` qui appelait `setState` : React rendait une première
   * fois avec la famille encore repliée, puis re-rendait aussitôt dépliée. Deux
   * passes pour un changement d'écran, et un clignotement visible sur la barre.
   *
   * Le motif employé ici est celui que React documente pour « ajuster un état
   * quand une entrée change » : mémoriser la valeur précédente, comparer au
   * rendu, corriger sur place. React reprend alors immédiatement le rendu avec
   * la nouvelle valeur, sans peindre l'état intermédiaire.
   *
   * ⚠️ LA SÉMANTIQUE NE CHANGE PAS : l'ouverture est un ÉVÉNEMENT (on arrive
   * sur l'écran), pas un état. La famille reste repliable à la main juste
   * après — c'est pourquoi on ne la DÉRIVE pas du `view`, ce qui la rouvrirait
   * à chaque rendu et rendrait le bouton de repli sans effet.
   *
   * ⚠️ On ne compare QUE le changement de famille, jamais `familleOuverte` :
   * replier à la main la famille où l'on se trouve doit tenir (retour d'Inès du
   * 17/08 — « Personnel & plannings » paraissait impossible à replier). C'est
   * aussi ce qui permet de garder une AUTRE famille ouverte pour la parcourir
   * sans quitter l'écran courant. */
  const idFamilleCourante = familleDe(view)?.id;
  const [famillePrecedente, setFamillePrecedente] = useState(idFamilleCourante);
  if (idFamilleCourante !== famillePrecedente) {
    setFamillePrecedente(idFamilleCourante);
    // L'accueil épinglé n'a pas de famille : on ne referme alors rien — sinon
    // un passage par le tableau de bord effacerait le repère qu'on venait d'ouvrir.
    if (idFamilleCourante) setFamilleOuverte(idFamilleCourante);
  }

  /**
   * Cloisonnement STRICT du défilement (décision du 12/08/2026) : la molette
   * posée sur la barre ne touche jamais à la page. C'est
   * `overscroll-behavior: contain`, mais posé UNIQUEMENT quand la barre déborde
   * vraiment — le cloisonner alors qu'il n'y a rien à y faire défiler
   * immobiliserait la page au seul motif que la souris est à gauche, ce qui se
   * lit comme une application gelée.
   *
   * ⚠️ La mesure se refait APRÈS CHAQUE RENDU, volontairement sans tableau de
   * dépendances. Une première version listait les causes supposées (repli des
   * familles, repli de la barre, écran courant) et se trompait : la hauteur
   * change aussi quand le magasin livre ses données — les droits RH font
   * apparaître des entrées, les pastilles s'affichent. La barre débordait donc
   * sans être cloisonnée. `setDeborde` ne renvoie pas de nouvel état quand la
   * valeur est identique : React interrompt la chaîne, il n'y a pas de boucle.
   */
  const colonne = React.useRef<HTMLElement | null>(null);
  const [deborde, setDeborde] = useState(false);
  const mesurerDebord = React.useCallback(() => {
    const el = colonne.current;
    // La tolérance d'un pixel évite de basculer sur un arrondi de zoom.
    if (el) setDeborde(el.scrollHeight > el.clientHeight + 1);
  }, []);
  useEffect(mesurerDebord);
  useEffect(() => {
    window.addEventListener('resize', mesurerDebord);
    return () => window.removeEventListener('resize', mesurerDebord);
  }, [mesurerDebord]);
  const pendingReq = demandes.filter((d) => !d.decision || d.decision === 'discuter').length;
  const withdrawn = docs.filter((d) => d.statut === 'retire').length;
  // Même définition qu'au registre (`ncOuverte`) : deux comptages divergents du
  // même mot feraient douter du badge autant que de l'écran.
  const openNC = ncs.filter(ncOuverte).length;
  // Lire l'heure PENDANT LE RENDU est impur (React 19 le signale) : le jour est
  // donc figé au montage. Ce n'est pas qu'une mise en conformité — comparer une
  // date d'échéance à minuit avec l'instant présent faisait basculer « en
  // retard » dès 00 h 01 le jour même de l'échéance. Une action due AUJOURD'HUI
  // n'est pas en retard : la comparaison de chaînes ISO le dit correctement.
  const [aujourdhui] = useState(() => aujourdhuiIso());
  const [dansSoixanteJours] = useState(
    () => isoJour(new Date(Date.now() + SEUIL_PEREMPTION_JOURS * 86400000)),
  );
  const lateAct = actions.filter((a) => a.echeance && !['realisee', 'verifiee'].includes(a.statut)
    && jourIso(a.echeance) < aujourdhui).length;
  const openRec = recls.filter((r) => r.statut !== 'cloturee').length;
  const lotAlertCount = lots.filter((l) => l.datePeremption && !['epuise', 'retire'].includes(l.statut)
    && jourIso(l.datePeremption) < dansSoixanteJours).length;
  // Cartes hors contrôle — modèle C2/C3. `cqis` (ancienne table) est vide
  // depuis la reprise : s'y référer donnerait un badge toujours à zéro.
  // R1 : une carte en BASELINE n'est pas hors contrôle — elle constitue sa cible.
  const cqiAlertCount = cartesCqi.filter((c) => c.statut !== 'conforme' && c.statut !== 'baseline').length;
  /* R3 (07/10/2026) — la même alerte que sur les lots de RÉACTIF (60 jours),
     étendue aux lots de CONTRÔLE ouverts : péremption proche ou dépassée. Le
     verdict du jour (`lotUtilisable`) vient du moteur, la date sert au préavis. */
  const cqiLotsAlertCount = cartesCqi.filter((c) => (c.lotUtilisable && !c.lotUtilisable.utilisable)
    || (c.datePeremption && jourIso(c.datePeremption) < dansSoixanteJours)).length
    // …et aux FLACONS et ALIQUOTS de contrôle, comptés par le moteur avec le
    // récapitulatif des cartes (`materiauxAEcheance`, même seuil de 60 jours).
    + (recapCqi?.flaconsAEcheance ?? 0) + (recapCqi?.aliquotsAEcheance ?? 0);
  // Le badge compte ce qui n'a pas encore été DÉCLARÉ : depuis que le
  // traitement d'une EEQ hors limites passe par une non-conformité, c'est la
  // NC qui atteste la prise en charge, plus l'action seule.
  const eeqAlertCount = eeqs.filter((x) => x.resultat === 'non_satisfaisant' && !x.ncRef).length;
  // Une dérogation à l'habilitation est une exception qui doit se voir : elle
  // remonte au niveau de la navigation, comme les retards et les NC ouvertes.
  const derogationsSemaine = rhAffectations.filter((a) => a.derogation).length;
  const demandesATraiter = rhAbsences.filter((a) => a.statut === 'demandee').length;
  const mesDemandesEnCours = mesAbsences.filter((a) => a.statut === 'demandee').length;
  // Pointage incomplet ou personne planifiée sans pointage : ces journées ne
  // doivent pas partir en paie sans que quelqu'un se soit prononcé.
  const presencesATraiter = rhJournees.filter((j) => !j.validee && ['anomalie', 'absence_non_pointee'].includes(j.etat)).length;
  // Un mois ouvert qui ne bloque sur rien est prêt à être clôturé : le signaler
  // évite qu'il traîne jusqu'à ce que le gestionnaire de paie le réclame.
  const paieAClore = rhPaie && rhPaie.statut === 'ouverte' && (rhPaie.controles?.journeesNonValidees ?? 0) === 0 && rhPaie.lignes.some((l) => l.joursTravailles > 0) ? 1 : 0;

  /** Pastilles, par écran. Les compteurs restent calculés ici — le modèle de
   *  navigation décrit la STRUCTURE, il n'a pas à connaître le magasin. */
  const pastilles: Partial<Record<GedView, { n?: number; couleur?: string }>> = {
    list: { n: docs.length, couleur: 'var(--gray-300)' },
    // Le badge des demandes ne parle qu'au RQ : pour les autres, le compteur
    // d'attente porterait sur des demandes qu'ils ne traitent pas.
    req: isRQ ? { n: pendingReq } : {},
    retrait: { n: withdrawn, couleur: 'var(--red-500)' },
    nc: { n: openNC },
    actions: { n: lateAct, couleur: 'var(--red-500)' },
    recl: { n: openRec },
    fourn: { n: lotAlertCount },
    cqi: { n: cqiAlertCount + cqiLotsAlertCount, couleur: 'var(--red-500)' },
    eeq: { n: eeqAlertCount, couleur: 'var(--red-500)' },
    mesconges: { n: mesDemandesEnCours || undefined },
    presences: { n: presencesATraiter, couleur: 'var(--red-500)' },
    planning: { n: derogationsSemaine, couleur: 'var(--amber-500)' },
    absences: { n: demandesATraiter },
    paie: { n: paieAClore || undefined, couleur: 'var(--amber-500)' },
  };

  const droits: DroitsNav = droitsNav(session, !!rhAcces?.acces);
  const familles = famillesBarre(droits);
  const familleCourante = familleDe(view);

  // En bande horizontale, les familles deviennent des MENUS (retour Ines).
  const horizontal = useBandeHorizontale();

  const rendreEntree = (e: EntreeNav, groupee: boolean) => {
    const p = pastilles[e.view];
    return (
      <NavItem key={e.view} view={e.view} icon={e.icon} label={e.label}
        badge={p?.n} badgeColor={p?.couleur}
        activeView={view} onGo={go} replie={replie} groupee={groupee} />
    );
  };

  return (
    <aside
      ref={colonne}
      // `data-deborde` commande le cloisonnement : la règle CSS ne s'applique
      // qu'à la barre qui a réellement de quoi défiler.
      data-deborde={deborde ? '1' : '0'}
      onFocus={() => onFocusDeplie(true)}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFocusDeplie(false); }}
      // `ged-nav-colonne` porte le collage ET la zone de défilement propre à la
      // barre : la souris fait défiler CE QU'ELLE SURVOLE (12/08/2026). Avant,
      // la barre suivait le défilement de la page — atteindre une entrée du bas
      // depuis un écran très long (maintenances, 85 lignes) obligeait à faire
      // défiler tout le contenu. Les valeurs vivent dans `globals.css` : la
      // hauteur disponible se déduit du collage, les deux ne doivent pas dériver.
      className="ged-nav-colonne"
      style={{ alignSelf: 'start', display: 'flex', flexDirection: 'column', gap: 2, width: replie ? 44 : undefined }}
      onScroll={() => setSurvol(null)}>
      {/* Replier n'a pas de sens en bande horizontale : elle EST la forme
          compacte, et le chevron y occupait une place pour ne rien offrir. */}
      {!horizontal && (
      <button
        onClick={onToggle}
        aria-label={repliee ? 'Déplier la navigation' : 'Replier la navigation'}
        title={`${repliee ? 'Déplier' : 'Replier'} la navigation (touche [)`}
        style={{
          /* COLLANT en tête de la barre (14/08/2026) : la barre a sa propre zone
             de défilement depuis le 12/08, et le bouton, simple premier enfant,
             sortait de l'écran dès qu'on descendait vers les entrées du bas.
             On pouvait donc déplier pour atteindre une entrée, puis ne plus
             trouver de quoi replier. Fond opaque : sans lui, les entrées
             défileraient visiblement sous le chevron.

             ⚠️ Le jeton est `--surface-page`, celui que `body` applique
             (globals.css). `--bg-page` n'a jamais existé : la variable absente
             retombait SANS BRUIT sur le blanc de secours, et le bouton se
             détachait en clair sur une barre en #F7FAFC. C'est le défaut type
             de la valeur de repli — rien n'échoue, la couleur est simplement
             fausse, et on la regarde des semaines sans la voir. */
          position: 'sticky', top: 0, zIndex: 3,
          background: 'var(--surface-page)',
          display: 'flex', alignItems: 'center',
          /* L'icône reste à la MÊME abscisse dépliée ou repliée — cf. la note
             sur `ICONE_CENTRE` plus bas. */
          justifyContent: replie ? 'center' : 'flex-start',
          gap: 6,
          width: '100%',
          padding: replie ? '8px 9px' : `8px 11px 8px ${ICONE_GAUCHE}px`,
          marginBottom: 4, border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
        }}>
        <I n={repliee ? 'chevron-right' : 'chevron-left'} s={14} />
      </button>
      )}
      {/* Le tableau de bord est ÉPINGLÉ hors des familles : c'est le point de
          retour, le seul écran qu'il ne doit jamais être possible de faire
          disparaître de la barre en repliant un groupe. */}
      {rendreEntree(ACCUEIL, false)}
      {/* Repliée en rail d'icônes, chaque famille n'est plus qu'un glyphe : son
          séparateur déplie ses rubriques, une famille à la fois. */}
      {/* BANDE HORIZONTALE — une famille = un menu qui s'ouvre en longueur.
          Sans ce cas, les 17 entrées se suivaient à plat sur trois écrans de
          défilement, séparateurs masqués : on ne savait ni où l'on était, ni
          de quelle famille on approchait. Le repli en rail (`replie`) n'a pas
          cours ici : la bande est déjà la forme compacte. */}
      {horizontal
        ? familles.map((f) => (
            <MenuFamille
              key={f.id} titre={f.titre} icon={f.icon} entrees={f.entrees}
              courante={familleCourante?.id === f.id}
              alertes={f.entrees.reduce(
                (n, e) => n + (pastilles[e.view]?.couleur === 'var(--gray-300)' ? 0 : pastilles[e.view]?.n || 0),
                0,
              )}
              rendreEntree={rendreEntree}
            />
          ))
        : replie
        ? familles.map((f, i) => {
            const ouverte = familleOuverte === f.id;
            const courante = familleCourante?.id === f.id;
            const alertes = f.entrees.reduce(
              (n, e) => n + (pastilles[e.view]?.couleur === 'var(--gray-300)' ? 0 : pastilles[e.view]?.n || 0),
              0,
            );
            return (
              <React.Fragment key={f.id}>
                {/* Le glyphe est centré sur la colonne des icônes (44 px) : c'est
                    l'axe que suivent les rubriques. Une alerte repliée reste visible
                    en point sur le glyphe. */}
                <button
                  type="button" className="ged-nav-sep" aria-expanded={ouverte}
                  aria-label={f.titre} title={ouverte ? `Replier ${f.titre}` : `Déplier ${f.titre}`}
                  onClick={() => setFamilleOuverte((o) => (o === f.id ? null : f.id))}
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '100%', margin: '6px 0 2px', padding: '6px 0', border: 'none', cursor: 'pointer',
                    background: ouverte ? 'var(--surface-sunken)' : 'transparent',
                    borderTop: `1px solid ${i === 0 ? 'transparent' : 'var(--border-subtle)'}`,
                    color: courante && !ouverte ? 'var(--color-primary)' : 'var(--text-muted)',
                  }}>
                  <I n={f.icon} s={13} sw={2} />
                  {!ouverte && alertes > 0 && (
                    <span aria-label={`${alertes} à traiter`} style={{ position: 'absolute', top: 4, right: 9, width: 6, height: 6, borderRadius: '50%', background: 'var(--red-500)' }} />
                  )}
                </button>
                {ouverte && f.entrees.map((e) => rendreEntree(e, false))}
              </React.Fragment>
            );
          })
        : familles.map((f, i) => {
            // La famille de l'écran courant s'OUVRE quand on y navigue (effet
            // plus haut), mais reste repliable à la main : la forcer ouverte
            // faisait passer son chevron pour cassé (retour d'Inès, 17/08).
            // Repliée, son titre garde la couleur d'accent — on sait toujours
            // où l'on est.
            const courante = familleCourante?.id === f.id;
            const ouverte = familleOuverte === f.id;
            // Les compteurs NEUTRES (le nombre de documents, en gris) ne sont
            // pas des alertes : les additionner ferait clignoter « Documents »
            // en rouge dès qu'il existe une procédure.
            const alertes = f.entrees.reduce(
              (n, e) => n + (pastilles[e.view]?.couleur === 'var(--gray-300)' ? 0 : pastilles[e.view]?.n || 0),
              0,
            );
            return (
              <React.Fragment key={f.id}>
                <TitreFamille
                  titre={f.titre} icon={f.icon} ouverte={ouverte} courante={courante} alertes={alertes}
                  premiere={i === 0}
                  // Accordéon STRICT : ouvrir referme les autres, et recliquer
                  // sur celle qui est ouverte la referme (sans quoi il y aurait
                  // toujours exactement une famille ouverte, et le chevron de
                  // celle-là ne servirait plus à rien).
                  onToggle={() => setFamilleOuverte((o) => (o === f.id ? null : f.id))}
                  onSurvol={(r) => ouvreSurvol(f.id, r)}
                  onQuitte={fermeSurvolDiffere}
                />
                {ouverte && f.entrees.map((e) => rendreEntree(e, true))}
                {!ouverte && survol && survol.id === f.id && createPortal((
                  <div role="menu" aria-label={f.titre}
                    onMouseEnter={gardeSurvol} onMouseLeave={fermeSurvolDiffere}
                    onClick={() => setSurvol(null)}
                    style={{
                      position: 'fixed', left: survol.gauche, top: survol.haut, bottom: survol.bas, zIndex: 120,
                      minWidth: 240, maxWidth: 300, maxHeight: survol.hauteur, overflowY: 'auto',
                      display: 'flex', flexDirection: 'column', gap: 1,
                      background: 'var(--surface-card, #fff)', border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', padding: 6,
                    }}>
                    {f.entrees.map((e) => rendreEntree(e, false))}
                  </div>
                ), document.body)}
              </React.Fragment>
            );
          })}
      {/* Le paramétrage Qualité s'ouvre par la roue crantée de la barre de
          connexion (en haut) : un second bouton ici faisait doublon. */}
    </aside>
  );
}

/** Fenêtre d'alerte avant péremption d'un lot de réactifs — VALEUR DE TRAVAIL.
 *
 *  Le même seuil sert aux matériaux reconstitués (`controllers/materiau.ts`) :
 *  deux valeurs différentes pour « bientôt périmé » finiraient par ne plus dire
 *  la même chose d'un écran à l'autre.
 *
 *  Mais il n'a **jamais été fixé par le laboratoire** : c'est un usage repris,
 *  pas une décision (arbitrage du 30/07/2026 — à lever avec eux). Le côté
 *  serveur porte déjà `seuilConfirme: false` et l'écran des matériaux le dit ;
 *  ici il ne s'agit que d'un compteur de badge, sans verdict. */
const SEUIL_PEREMPTION_JOURS = 60;

/**
 * Le chemin parcouru de fiche en fiche, cliquable à rebours.
 *
 * Il ne s'affiche QUE lorsqu'on est entré dans une fiche depuis une autre :
 * un fil d'Ariane permanent sur un écran atteint par la barre latérale
 * répéterait le titre déjà lu juste dessous.
 */
function FilRetour() {
  const { pileRetour, retourPile } = useGed();
  if (!pileRetour.length) return null;
  const precedente = pileRetour[pileRetour.length - 1];
  return (
    <nav aria-label="Chemin parcouru" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 12, fontSize: 12.5 }}>
      <button
        onClick={retourPile} className="ged-btn"
        title={`Revenir à ${precedente.libelle}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--border-default)',
          background: '#fff', color: 'var(--text-body)', borderRadius: 'var(--radius-pill)',
          padding: '4px 12px 4px 9px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'var(--font-sans)',
        }}>
        <I n="chevron-left" s={13} sw={2} />Revenir à {precedente.libelle}
      </button>
      {/* Le chemin complet en toutes lettres : savoir d'où l'on vient vaut
          autant que pouvoir y retourner, quand on est trois fiches plus loin. */}
      {pileRetour.length > 1 && (
        <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
          {pileRetour.map((e) => e.libelle).join(' › ')} › …
        </span>
      )}
    </nav>
  );
}

/**
 * LE PLAN DE LA BANNIÈRE — la valeur au-dessus de laquelle toute fenêtre
 * modale doit se poser.
 *
 * ⚠️ Elle était écrite en clair au milieu du rendu, et cinq surcouches
 * s'étaient posées EN DESSOUS sans que rien ne le signale : le tiroir du
 * dossier d'une personne y perdait ses premiers pixels — nom, badge « Sorti
 * des effectifs » et boutons d'action —, et les quatre autres, centrées, ne
 * montraient rien du tout. Plus grave que l'affichage : leur voile s'arrêtait
 * lui aussi sous la bannière, qui restait donc claire ET CLIQUABLE pendant
 * qu'une fenêtre se déclarait `aria-modal`. On pouvait changer de module sans
 * jamais l'avoir fermée.
 *
 * Nommée, elle se compare : `backend/tests/qualite-plans-modales.test.js` la
 * lit ICI et refuse toute surcouche modale posée plus bas. Signalé par la RQ
 * le 24/08/2026 sur l'organigramme.
 */
export const PLAN_BANNIERE = 80;

/**
 * Les écrans qui méritent toute la largeur de la fenêtre.
 *
 * Ce sont des GRILLES : l'information y est en colonnes, et chaque colonne
 * retirée se paie en troncature. Partout ailleurs, le plafond de 1280 px est
 * un choix de lisibilité, pas une limite technique.
 */
const LARGES = new Set(['planning', 'presences', 'hab']);

/* ══════════════════════════════════════════════════════════════════════════
 * DÉCONNEXION AUTOMATIQUE DE QUALITÉ (RQ, 04/10/2026).
 *
 * Le réglage existait (Paramétrage → Utilisateurs, « délai d'inactivité ») et
 * le socle aussi (`DeconnexionAuto`) — mais SEUL le site public l'appliquait :
 * une session Qualité restait ouverte indéfiniment sur un poste partagé, et
 * tout ce qui s'y signait portait le nom de la personne partie.
 *
 * Même délai que le site (`field-config.org.sessionTimeoutMin`, 0 = désactivé).
 * Délai illisible (droit, panne) → 30 min : en cas de doute on protège, on ne
 * laisse pas ouvert.
 *
 * ⚠️ On ne coupe JAMAIS pendant une saisie : tant qu'une fenêtre (dialogue) est
 * ouverte, l'avertissement reste affiché et la coupure attend — perdre un
 * formulaire à moitié rempli est ce qui fait contourner la mesure.
 * ══════════════════════════════════════════════════════════════════════════ */
function DeconnexionQualite() {
  const session = useGed((s) => s.session);
  const [delai, setDelai] = useState<number | null>(null);
  useEffect(() => {
    if (!session.authenticated) return;
    let vivant = true;
    void fetch('/api/politique', { credentials: 'include', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setDelai(Number.isFinite(Number(d?.sessionTimeoutMin)) ? Number(d.sessionTimeoutMin) : 30); })
      .catch(() => { if (vivant) setDelai(30); });
    return () => { vivant = false; };
  }, [session.authenticated]);
  if (!session.authenticated || delai === null) return null;
  return (
    <DeconnexionAuto
      delaiMinutes={delai}
      onDeconnexion={async () => {
        await fetchEcriture('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
        window.location.href = '/';
      }}
      retenirSi={() => (document.querySelector('[role="dialog"]') ? 'Une fenêtre de saisie est ouverte' : null)}
    />
  );
}

export function Shell({ children }: { children: React.ReactNode }) {
  // Adresses des autres modules résolues à l'exécution (posées par le layout).
  const { site: SITE_URL, manuel: MANUEL_URL } = useUrlsModules();
  const { boot, booted, session, view, toast, toastSeverite, toastAction, dismissToast, confirmationGlobale, repondreConfirmation, go, openSettings, select, openDoc, backendError, erreurLien } = useGed();
  /* L'APPAREIL OUVERT accompagne la déclaration (13/10/2026) : depuis la fiche
     d'un équipement, la NC propose de le relier — à un clic, jamais d'office. */
  const appareilOuvert = useGed((st) => (st.view === 'equip' && st.selectedEquip
    ? st.equips.find((e) => e.documentId === st.selectedEquip)?.code || '' : ''));
  useEffect(() => { void boot(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Adresse ↔ état : c'est ce branchement qui rend « Précédent », « Suivant »,
  // F5, les favoris et les liens partagés opérants. Monté ICI et une seule
  // fois : deux montages pousseraient deux entrées d'historique par navigation.
  useAdresse();

  /* LE DÉSTOCKAGE D'UN ALIQUOT, en commande flottante (RQ, 03/10/2026) — le
     geste quotidien du poste, à portée depuis tout écran comme la NC. Proposé
     à qui peut MODIFIER la rubrique des matériaux, et à personne d'autre : la
     commande n'est pas grisée, elle est absente (la pile se resserre). Le
     moteur revalide de toute façon (garde de section `mat`). */
  const { rhAcces: rhAccesPile } = useGed();
  const [destockageOuvert, setDestockageOuvert] = useState(false);
  const peutDestocker = droitsNav(session, !!rhAccesPile?.acces).section('mat', 'modifier');

  // Changer d'écran REMET EN HAUT. Sans cela, on arrivait sur un écran déjà
  // défilé — le bandeau collant au milieu de la fenêtre, un grand vide
  // au-dessus, et le titre de l'écran hors champ (constat de recette A5). Le
  // navigateur ne le fait pas seul : c'est une application d'une seule page,
  // rien ne « charge » entre deux écrans.
  //
  // SAUF au RETOUR (12/08/2026) : revenir n'est pas arriver. `scrollARestaurer`
  // porte alors l'endroit où l'on lisait, reposé après le rendu de l'écran
  // retrouvé — dans un `requestAnimationFrame`, le temps que la page ait repris
  // sa hauteur, sinon le navigateur borne la position à celle d'un écran vide.
  useEffect(() => {
    const y = useGed.getState().scrollARestaurer;
    if (y == null) { window.scrollTo({ top: 0, behavior: 'auto' }); return undefined; }
    const id = requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: 'auto' });
      // Les COLONNES aussi : sur un écran maître-détail, c'est là qu'on lisait
      // (la page, elle, n'avait pas bougé). Reposé dans la même image que le
      // défilement de page, une fois l'écran retrouvé rendu.
      reposerScrolls(useGed.getState().scrollsInternesARestaurer);
      useGed.getState().scrollRestaure();
    });
    return () => cancelAnimationFrame(id);
  }, [view]);

  // Repli en icônes (G3), état porté ICI : la largeur de colonne de la grille
  // en dépend, pas seulement le rendu interne de la barre.
  /* Les zones d'écriture prennent la taille de leur contenu. Posé UNE fois ici
     pour les 111 du module — et pour celles qui n'existent pas encore. Ne fait
     rien sur un navigateur qui connaît `field-sizing: content`. */
  useZonesAuto();

  const [repliee, setRepliee] = useState(() => (typeof window !== 'undefined' && localStorage.getItem(CLE_REPLI) === '1'));
  const [depliePourFocus, setDepliePourFocus] = useState(false);
  const replie = repliee && !depliePourFocus;
  useEffect(() => { localStorage.setItem(CLE_REPLI, repliee ? '1' : '0'); }, [repliee]);
  const pageLarge = usePageLarge();
  const cadreRef = useRef<HTMLDivElement>(null);
  const largeurContenu = useLargeurContenu(cadreRef);

  /**
   * Basculer le repli — et ANNULER le dépliage d'accessibilité (13/08/2026).
   *
   * Le bouton « Replier » vit DANS la barre : le cliquer y met le focus, et un
   * focus déplie la barre pour qui navigue au clavier (`onFocusDeplie`). Le
   * repli demandé était donc défait dans le même geste, et le bouton paraissait
   * ne rien faire — une fois sur deux, selon l'endroit d'où venait le focus.
   * Un geste EXPLICITE prime sur l'assistance : on lève le dépliage de focus.
   */
  const basculerRepli = React.useCallback(() => {
    setDepliePourFocus(false);
    setRepliee((r) => !r);
  }, []);

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== '[' || dansUnChamp(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;
      basculerRepli();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [basculerRepli]);

  // Deep-link ?view=<écran> : arriver directement sur un écran GED (utilisé par
  // le centre de paramétrage et les liens inter-modules). Consommé une fois.
  //
  // ?equip=<code> ouvre directement la fiche d'un équipement : c'est la cible
  // des QR d'étiquette. Le technicien scanne la vignette collée sur l'automate
  // et arrive sur sa fiche de vie — sans quoi l'étiquette ne serait qu'un
  // numéro d'inventaire de plus.
  useEffect(() => {
    if (!booted) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const v = params.get('view');
      const eq = params.get('equip');
      // ?doc=<code> ouvre LA fiche du document : cible des tâches « accusé de
      // lecture attendu » et « document à signer », qui désignent une pièce
      // précise et non l'écran qui la contient.
      const dc = params.get('doc');
      if (dc) {
        // Code inconnu (document supprimé depuis l'envoi de la notification) :
        // on ouvre la liste plutôt qu'une fiche vide.
        if (useGed.getState().docs.some((d) => d.code === dc)) openDoc(dc); else go('list');
      } else if (eq) {
        const cible = useGed.getState().equips.find((x) => x.code === eq);
        // Code inconnu : on ouvre quand même le parc plutôt que de laisser le
        // scan sans effet — l'étiquette peut désigner un appareil réformé.
        if (cible) select('selectedEquip', cible.documentId);
        go('equip');
      } else if (v) {
        // ?view=settings ouvre la fenêtre flottante — ce n'est plus une vue.
        if (v === 'settings') openSettings(); else go(v as never);
      }
      /* ?ref=<référence> : la PIÈCE à ouvrir sur cet écran (17/08).
       *
       * ⚠️ CETTE LECTURE ÉTAIT ENFERMÉE DANS LE `else if (v)` ci-dessus, et
       * c'est ce qui la rendait inatteignable depuis un CHEMIN (13/09/2026).
       * Le centre des tâches vise désormais `/maintenances?ref=12@2026-09-10`
       * — l'écran par son chemin, l'occurrence par la requête — parce que
       * `12@2026-09-10` n'est la référence d'aucune fiche : la mettre dans le
       * chemin ne désignerait rien. Laissée à l'intérieur du `else if`, elle
       * n'aurait jamais été lue, et la seule tâche qui fonctionnait aurait
       * cessé de fonctionner.
       *
       * L'écran d'arrivée la CONSOMME s'il sait la lire, l'ignore sinon — et
       * affiche alors sa liste, jamais une erreur. */
      const rf = params.get('ref');
      if (rf) useGed.getState().setRefCible(rf);
      /* Consommée une fois : sans ce nettoyage, un rechargement (F5) rouvrirait
         la même pièce, et l'adresse resterait celle d'une notification plutôt
         que celle de l'écran où l'on se trouve. */
      if (v || eq || dc || rf) window.history.replaceState(null, '', window.location.pathname);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booted]);

  if (!booted) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 14 }}>Chargement…</div>;
  }

  // Hors forfait : le laboratoire n'a pas souscrit au module. Écran distinct —
  // proposer « Se connecter » ici serait faux (l'utilisateur EST connecté) et
  // le renverrait en boucle.
  if (session.moduleDisabled) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: 'center', background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: 'clamp(28px,5vw,44px)' }}>
          <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 'var(--radius-md)', background: 'var(--gray-100)', color: 'var(--text-muted)', alignItems: 'center', justifyContent: 'center' }}><I n="shield" s={24} sw={1.6} /></span>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-strong)', margin: '18px 0 0' }}>Module Qualité non inclus</h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-body)', margin: '12px 0 22px' }}>
            Le forfait de votre laboratoire ne comprend pas la gestion documentaire.
            Le super admin peut l’ajouter depuis <strong>Paramétrage → Forfait &amp; modules</strong>.
          </p>
          <a href={MANUEL_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--surface-sunken)', color: 'var(--text-body)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 20px', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
            <I n="arrow" s={16} />Retour au manuel
          </a>
        </div>
      </div>
    );
  }

  // Pas connecté : l'app Qualité se connecte elle-même désormais — elle n'a
  // plus besoin de renvoyer vers le manuel (qui n'est pas forcément démarré).
  if (!session.authenticated) {
    return <LoginView />;
  }

  // Accès réservé : compte connecté, mais module Qualité désactivé (H4) ou
  // aucun rôle GED. Ici un formulaire de connexion serait faux — la personne
  // EST connectée, il lui manque un droit que seul le RQ peut accorder.
  if (session.apps?.ged === false || !session.roles.length) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 460, textAlign: 'center', background: '#fff', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', padding: 'clamp(28px,5vw,44px)' }}>
          <span style={{ display: 'inline-flex', width: 52, height: 52, borderRadius: 'var(--radius-md)', background: 'var(--navy-900)', color: 'var(--cyan-300)', alignItems: 'center', justifyContent: 'center' }}><I n="shield" s={24} sw={1.6} /></span>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-strong)', margin: '18px 0 0' }}>Espace qualité — accès réservé</h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-body)', margin: '12px 0 22px' }}>
            Votre profil ne dispose d’aucun rôle qualité. Contactez le Responsable Qualité.
          </p>
          <a href={SITE_URL} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--surface-sunken)', color: 'var(--text-body)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '12px 20px', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>
            <I n="arrow" s={16} />Retour au site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface-page)', fontFamily: 'var(--font-sans)', color: 'var(--text-body)' }}>
      {/* ⚠️ TOUT PREMIER élément focalisable de la page, avant la barre de
          connexion comme avant la barre latérale : un lien d'évitement placé
          plus bas ne saute que ce qui le suit. Il ne se voit qu'au clavier
          (feuille `globals.css`, `.ged-evitement`). */}
      <a href="#contenu-principal" className="ged-evitement">Aller au contenu</a>
      {/* Plus de sous-en-tête de module : il répétait le nom du laboratoire déjà
          présent dans la barre de connexion et occupait une bande sur toute la
          largeur pour trois informations fixes. Les rôles, seule donnée utile
          qu'il portait, ont rejoint l'infobulle du profil — visibles au survol,
          sans coûter de hauteur à chaque écran. */}
      <div style={{ position: 'sticky', top: 0, zIndex: PLAN_BANNIERE }}>
        <ConnectionBar />
        <DeconnexionQualite />
      </div>
      {/* Backend injoignable au dernier chargement : un bandeau global prévient
          que les écrans peuvent apparaître vides à tort. Sans lui, « Aucune
          carte de contrôle » (502) était indistinguable d'une base vide — un RQ
          pouvait conclure à une perte de données (constat QA majeur du 30/07). */}
      {backendError && (
        <div role="alert" style={{ maxWidth: 1280, margin: '12px auto 0', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)', border: '1px solid var(--red-200, #F1C9C7)', color: 'var(--red-700, #B3261E)', fontSize: 13, fontWeight: 500 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
            <span>Serveur momentanément injoignable. Les écrans peuvent apparaître vides ou incomplets — il ne s’agit pas d’une perte de données. Réessayez dans un instant.</span>
            <button type="button" onClick={() => void useGed.getState().refresh()} style={{ marginInlineStart: 'auto', flexShrink: 0, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, color: 'var(--red-700, #B3261E)', background: 'transparent', border: '1px solid currentColor', borderRadius: 'var(--radius-sm)', padding: '5px 12px', cursor: 'pointer' }}>Réessayer</button>
          </div>
        </div>
      )}
      {/* LIEN REÇU QUI NE MÈNE NULLE PART.
          Le destinataire d'un lien partagé n'a aucun autre moyen de comprendre
          ce qui se passe : sans ce bandeau, il atterrit sur un tableau de bord
          ou une liste et croit s'être trompé de clic. Deux causes possibles —
          la fiche n'existe pas, ou elle ne lui est pas accessible — et on ne
          peut pas les distinguer côté écran : le message le DIT plutôt que de
          trancher au hasard, et renvoie vers le RQ. */}
      {erreurLien && (
        <div role="alert" style={{ maxWidth: 1280, margin: '12px auto 0', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '11px 16px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50, #FFF8EC)', border: '1px solid var(--amber-300, #EFC77B)', color: 'var(--amber-700, #8A5B00)', fontSize: 13, lineHeight: 1.5 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }}><circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" /></svg>
            <span style={{ flex: 1 }}>{erreurLien}</span>
            <button type="button" onClick={() => useGed.setState({ erreurLien: null })}
              aria-label="Fermer l’avertissement"
              style={{ flexShrink: 0, background: 'transparent', border: '1px solid currentColor', color: 'inherit', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, padding: '4px 10px', cursor: 'pointer' }}>Fermer</button>
          </div>
        </div>
      )}
      {/* ⚠️ `minmax(0, 1fr)` et NON `1fr` — la colonne du contenu DOIT pouvoir
          se rétrécir.
          `1fr` vaut `minmax(auto, 1fr)`, et ce minimum `auto` se résout en
          MIN-CONTENT : la colonne refuse alors de descendre sous la largeur
          intrinsèque de son plus large enfant. Un tableau à huit colonnes, une
          rangée de compteurs, et la grille pousse au-delà du conteneur de
          1280 px et de la fenêtre elle-même.
          Symptôme signalé le 18/08, capture à l'appui, sur les
          non-conformités en production : le bouton « Déclarer une NC » aligné
          à droite sortait de l'écran, le quatrième compteur était coupé, et
          les dernières colonnes du tableau étaient hors champ. La page ne se
          « déséquilibrait » pas — elle DÉBORDAIT.
          Le piège est déjà documenté dans `Dashboard.tsx` pour ses grilles
          internes ; il avait été manqué ici, sur la grille qui porte TOUS les
          écrans. */}
      {/* ⚠️ LA MARGE BASSE RÉSERVE LA PLACE DES COMMANDES FLOTTANTES (relevé
          du 27/08/2026, P4e). Elles recouvraient la DERNIÈRE ligne de chaque
          écran — sur les maintenances la colonne « Acquitter », à 135 % de zoom
          la colonne « Statut ». Une commande permanente doit réserver sa place,
          pas la prendre à ce qui est déjà là.
          03/10/2026 : la rangée horizontale (46 px de haut, ~300 de large) est
          devenue une PILE VERTICALE de ronds de 46 px (RQ : déstockage, idée,
          NC). Plus étroite — elle ne couvre plus que les ~46 px du bord droit —
          mais plus HAUTE : 22 + n × 46 + (n − 1) × 10. Or c'est précisément le
          bord droit qui porte les colonnes d'action des tableaux : réserver à
          DROITE aurait rétréci TOUS les écrans pour un coin. On réserve donc en
          BAS, à la hauteur réelle de la pile + 14 px : 138 px pour deux
          commandes, 194 pour trois. Le surcroît n'existe qu'en fin de page. */}
      {/* ── LA LARGEUR DU PLANNING (Lot 1, 02/09/2026) ─────────────────────
          Mesuré le 01/09 : le tableau du planning occupait 984 px dans une
          fenêtre de 1463. Ce n'était pas le tableau — il est déjà en
          `width: 100%` — c'était CE conteneur : 1280 − 48 de marge − 222 de
          barre − 24 d'écart = 986 px. Sur sept colonnes de jour, il restait
          50 px utiles pour un prénom, et tout ce qui dépassait sept caractères
          se coupait.

          ⚠️ LE PLAFOND N'EST LEVÉ QUE POUR LES ÉCRANS EN COLONNES : les grilles
          (`LARGES`) et, depuis le 21/10/2026 (Ines), tout écran qui affiche un
          REGISTRE — le tableau le signale lui-même (`usePageLarge`). Une ligne
          de texte lue sur 1600 px n'est pas plus lisible, elle l'est moins — la
          mesure de lecture confortable tourne autour de 80 caractères, et c'est
          ce que 1280 px donne. On n'élargit donc pas « pour uniformiser » : on
          élargit là où l'information est en colonnes et où la largeur manque
          vraiment. */}
      <div ref={cadreRef} className="ged-layout" style={{ maxWidth: LARGES.has(view) || pageLarge ? 1760 : 1280, margin: '0 auto', padding: `20px 24px ${peutDestocker ? 194 : 138}px`, display: 'grid', gridTemplateColumns: `${replie ? 52 : LARGEUR_BARRE}px minmax(0, 1fr)`, gap: GOUTTIERE, alignItems: 'start', transition: 'grid-template-columns 0.15s ease' }}>
        <Sidebar replie={replie} repliee={repliee} onToggle={basculerRepli} onFocusDeplie={setDepliePourFocus} />
        {/* `id` et `tabIndex={-1}` : la cible d'un lien d'évitement doit pouvoir
            RECEVOIR le focus, sinon le navigateur y fait défiler la page mais
            laisse le clavier là où il était — et la tabulation suivante
            repart dans la barre latérale, c'est-à-dire que le lien n'a rien
            évité. `-1` la rend focalisable par programme sans l'ajouter à
            l'ordre de tabulation.

            ⚠️ `maxWidth: LARGEUR_CONTENU` (relevé du 27/08/2026, P3c) : replier
            la barre latérale rendait 170 px au contenu, et TOUTES les colonnes
            de tableau se recalculaient. Comme l'état du repli est mémorisé par
            navigateur, la même page n'avait pas la même tête d'une session à
            l'autre, ni d'un poste à l'autre — on ne pouvait pas dire « la
            colonne du milieu » à quelqu'un au téléphone.
            Le contenu garde donc la largeur de la barre DÉPLIÉE, qui est la
            plus étroite des deux : replier ne gagne plus de place, mais c'est
            ce qu'on cherche — replier allège la vue, ça ne la redessine pas. */}
        <main id="contenu-principal" tabIndex={-1} style={{ minWidth: 0, width: '100%', maxWidth: largeurContenu ?? LARGEUR_CONTENU, outline: 'none' }}><FilRetour />{children}</main>
      </div>
      <SettingsModal />
      {/* Déclarer une NC depuis n'importe quel écran, sans naviguer. La source
          est déduite de l'écran courant : on ne demande pas au déclarant de
          classer ce qu'il vient de voir. */}
      {/* Commande d'élargissement des écrans maître-détail. Montée ICI, une
          seule fois : elle détecte elle-même la grille à deux colonnes, et
          couvre donc les six écrans actuels comme ceux à venir. */}
      <AgrandirColonne />
      {/* Les fiches liées s'ouvrent PAR-DESSUS l'écran, qui reste monté. */}
      <PanneauFiche />
      {/* ⚠️ LE MÊME FORMULAIRE QU'AU REGISTRE (RQ, 05/10/2026). La gommette est
          un RACCOURCI vers « déclarer une NC » de la rubrique, pas un second
          formulaire : elle ouvre celui-ci en fenêtre. Il vit dans la GED —
          magasin, taxonomie des processus, critères d'acceptation d'échantillon
          — et le socle partagé ne peut pas l'importer ; il lui est donc INJECTÉ
          (cf. `formulaireNC` dans `GommetteNC`). */}
      <GommetteNC contexte={{ ...contexteNC(view), ...(appareilOuvert ? { equipementCode: appareilOuvert } : {}) }} onDeclaree={() => void useGed.getState().refresh()}
        formulaireNC={({ contexte, onFait }) => (
          <DeclareForm
            encadre={false}
            contexte={contexte}
            onDone={onFait}
            /* La fiche créée s'ouvre depuis le toast. La référence vient
               d'être attribuée : le registre ne la connaît qu'après le
               rafraîchissement lancé par `onDeclaree`, d'où la lecture du
               magasin AU CLIC et non à la création. Introuvable, on pose au
               moins le registre — la référence, elle, a été annoncée. */
            onOuvrir={(ref) => {
              const cible = useGed.getState().ncs.find((n) => n.ref === ref);
              useGed.getState().naviguerVers('nc', cible ? { cle: 'selectedNC', valeur: cible.documentId } : undefined);
            }}
          />
        )}
        commandes={peutDestocker ? [{
          cle: 'destockage', libelle: 'Déstocker un aliquot',
          titre: 'Déstocker un aliquot de contrôle (scan ou recherche)',
          icone: <I n="tube" s={19} />, onClick: () => setDestockageOuvert(true),
        }] : []} />
      {destockageOuvert && <DestockageFenetre onClose={() => setDestockageOuvert(false)} />}
      {/* PAS de pied de page (RQ, 19/08/2026) : le footer vitrine — contact,
          navigation, mention d'accréditation — s'adresse à des visiteurs. Ici
          tout le monde est du laboratoire, connecté, et la barre d'en-tête
          porte déjà les liens vers le site et le catalogue. Le bandeau ne
          faisait qu'allonger chaque écran d'un aplat sombre inutile. */}
      {/* Une erreur et une confirmation partageaient le même fond, la même durée
          et aucun rôle ARIA : « Diffusion émise » et « Séparation des tâches :
          refusé » étaient indistinguables (QA v2 UX-03). L'erreur est désormais
          rouge, annoncée aux lecteurs d'écran, et ne part qu'à la fermeture. */}
      <TravailEnCours />
      <AnnonceDuJour />
      {confirmationGlobale && (
        <Modale titre={confirmationGlobale.titre} onClose={() => repondreConfirmation(false)} largeur={440}
          pied={(
            <PiedActions
              taille="sm"
              annuler={{ onClick: () => repondreConfirmation(false) }}
              principale={{ libelle: confirmationGlobale.confirmer, variante: confirmationGlobale.danger ? 'danger' : 'primaire', onClick: () => repondreConfirmation(true) }}
            />
          )}>
          <p style={{ fontSize: 13, color: 'var(--text-strong)', margin: 0, lineHeight: 1.55, whiteSpace: 'pre-line' }}>{confirmationGlobale.question}</p>
        </Modale>
      )}
      {toast && (() => {
        // Trois cas, trois couleurs. L'INDÉTERMINÉ n'est pas rouge à dessein :
        // rien n'a été refusé, et le peindre comme une erreur ferait croire à
        // un échec là où la question est « est-ce passé ? ». L'ambre dit
        // l'incertitude, qui est exactement ce qu'on a à dire.
        const persistant = toastSeverite === 'erreur' || toastSeverite === 'indetermine';
        const fond = toastSeverite === 'erreur' ? 'var(--red-600, #c02942)'
          : toastSeverite === 'indetermine' ? 'var(--amber-700, #8A5B00)'
            : 'var(--navy-900)';
        return (
          <div
            role={persistant ? 'alert' : 'status'}
            aria-live={persistant ? 'assertive' : 'polite'}
            style={{
              /* ⚠️ LA COUCHE LA PLUS HAUTE, sans exception (25/08/2026). À 120,
                 ce bandeau passait sous les vingt-quatre empilements du module :
                 toute fenêtre modale (400), le plein écran bureautique (900),
                 la norme (950). « Connexion interrompue. Votre saisie a
                 peut-être été enregistrée » devenait illisible — le seul
                 message dont dépend le fait de NE PAS refaire l'opération. */
              position: 'fixed', bottom: 22, left: '50%', transform: 'translateX(-50%)', zIndex: COUCHES.MESSAGE,
              display: 'flex', alignItems: 'center', gap: 12, maxWidth: 'min(620px, calc(100vw - 32px))',
              background: fond, color: '#fff',
              borderRadius: persistant ? 'var(--radius-md)' : 'var(--radius-pill)',
              padding: persistant ? '12px 14px 12px 16px' : '10px 18px',
              fontSize: 13.5, fontWeight: 600, lineHeight: 1.45, boxShadow: 'var(--shadow-lg)',
            }}
          >
            <span>{toast}</span>
            {persistant && toastAction && (
              <button
                onClick={toastAction.faire}
                style={{ flex: '0 0 auto', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)', color: '#fff', borderRadius: 'var(--radius-sm)', padding: '5px 11px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap' }}
              >
                {toastAction.label}
              </button>
            )}
            {persistant && (
              <button
                onClick={dismissToast}
                aria-label="Fermer le message"
                style={{ flex: '0 0 auto', background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', borderRadius: 6, width: 24, height: 24, fontSize: 15, lineHeight: 1, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}
              >
                ×
              </button>
            )}
          </div>
        );
      })()}
    </div>
  );
}
